import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';

function SamplesPage() {
  const [showCart, setShowCart] = useState(false);

  // Use global cart context
  const { cart, addGameToCart, removeFromCart, getTotalPrice } = useCart();

  // Handle Checkout
  const handleCheckout = () => {
    if (cart.length === 0) {
      alert('Your cart is empty.');
      return;
    }
    alert('¡Excelente elección! Por favor, envíanos un mensaje para agendar tus horas de tutoría ahora mismo.');
    // Redirect to contact page or WhatsApp (simulated)
    window.location.href = '/contact';
  };

  // Tutoring packs data
  const tutoringPacks = [
    {
      id: 1,
      name: "Quick Spark Session",
      duration: "30 Minutes",
      description: "Perfect for a quick doubt resolution or a brief conversation practice to start your day.",
      image: "/appiconos/vocab.webp",
      category: "Intro",
      price: 15.00,
      features: ["Native Tutor", "Focused Learning", "Video Call", "Learning Material Included"],
      stars: 4.9
    },
    {
      id: 2,
      name: "Standard Deep Dive",
      duration: "1 Hour",
      description: "The classic intensive session. Deep dive into grammar, vocabulary, or culture at your own pace.",
      image: "/appiconos/sentence.webp",
      category: "Individual",
      price: 25.00,
      features: ["Native Tutor", "Custom Curriculum", "PDF Summary", "Recording Available"],
      stars: 4.8
    },
    {
      id: 3,
      name: "Perfect Duo Pack",
      duration: "2 Hours",
      description: "Two intensive hours to master a specific topic. Can be split into two 1-hour sessions.",
      image: "/appiconos/puzzle.webp",
      category: "Bundle",
      price: 45.00,
      features: ["Save 10% over single", "Flexible Scheduling", "Native Tutor", "Homework Assignments"],
      stars: 4.7
    },
    {
      id: 4,
      name: "Trio Mastery Bundle",
      duration: "3 Hours",
      description: "Three hours of personalized attention. Ideal for preparing for an exam or a trip.",
      image: "/appiconos/mind.webp",
      category: "Bundle",
      price: 60.00,
      features: ["Save 20% over single", "Priority Booking", "Native Tutor", "Expert Feedback"],
      stars: 4.9
    },
    {
      id: 5,
      name: "Explorer Five",
      duration: "5 Hours",
      description: "Five hours of total immersion. Explore the Spanish language with consistency and depth.",
      image: "/appiconos/poco.webp",
      category: "Premium",
      price: 95.00,
      features: ["Best for Consistency", "Personal Portal", "Native Tutor", "Exclusive Resources"],
      stars: 4.8
    },
    {
      id: 6,
      name: "Success Ten Pack",
      duration: "10 Hours",
      description: "The complete transformation package. Ten hours to reach your next level of fluency.",
      image: "/appiconos/game.webp",
      category: "Premium",
      price: 180.00,
      features: ["VIP Support", "Progress Tracking", "Native Tutor", "Certificate of Hours"],
      stars: 5.0
    },
    {
      id: 7,
      name: "Fluency Pro Immersion",
      duration: "20 Hours",
      description: "The ultimate fluency accelerator. 20 hours of high-impact learning for rapid results.",
      image: "/appiconos/recetario.webp",
      category: "Elite",
      price: 320.00,
      features: ["Best Value per Hour", "Tailored Roadmap", "Native Tutor", "Lifetime Materials Access"],
      stars: 4.9
    },
    {
      id: 8,
      name: "Vencejo VIP Monthly",
      duration: "4 Hours/Month",
      description: "Our core membership. One hour a week to keep your Spanish sharp and constantly improving.",
      image: "/appiconos/historianas.webp",
      category: "Membership",
      price: 80.00,
      isSubscription: true,
      features: ["Weekly Consistency", "Auto-renewal", "Native Tutor", "Academy Community Access"],
      stars: 4.9
    }
  ];

  const handleAddToCart = (pack) => {
    // We can repurpose addGameToCart since it handles general items
    const plan = {
        price: pack.price,
        period: pack.duration
    };
    addGameToCart({
        id: `tutor-${pack.id}`,
        name: pack.name,
        image: pack.image
    }, pack.id, plan);
    setShowCart(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Shared Header Component */}
      <Header 
        onCartClick={() => setShowCart(!showCart)} 
        showCart={true}
      />

      {/* Hero Section */}
      <section 
        className="relative py-24 lg:py-32 overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/images/spanish.learn.webp)',
          backgroundColor: '#FEF9C3'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-100/95 via-secondary-50/92 to-primary-50/95"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-7xl font-display font-bold text-primary-600 mb-6 leading-tight">
            Tutoring Hub
          </h1>
          <h2 className="text-2xl md:text-4xl font-display font-semibold text-gray-700 mb-8">
            Personalized 1-on-1 Spanish Lessons
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
            Book your sessions with native experts and start speaking Spanish naturally today. 
            Flexible schedules tailored to your needs.
          </p>
        </div>
      </section>

      {/* Tutoring Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {tutoringPacks.map((pack) => (
                <div 
                  key={pack.id} 
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col"
                >
                    {/* Category Badge */}
                    <div className="p-5 pb-0">
                      <div className="bg-primary-600 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider inline-block shadow-md">
                        {pack.category}
                      </div>
                    </div>
                    
                    {/* Pack Content */}
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-bold text-gray-900 leading-tight">{pack.name}</h3>
                        <div className="flex items-center text-amber-500">
                          <span className="text-sm font-bold">★ {pack.stars}</span>
                        </div>
                      </div>
                      
                      <div className="inline-block bg-primary-50 text-primary-700 text-xs font-bold px-3 py-1 rounded-full mb-3 self-start">
                         {pack.duration}
                      </div>
                      
                      <p className="text-gray-600 text-sm mb-5 leading-relaxed flex-1">
                        {pack.description}
                      </p>

                      <div className="mb-5 bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <ul className="space-y-1">
                          {pack.features.map((feature, idx) => (
                            <li key={idx} className="text-[10px] text-gray-500 flex items-center">
                              <span className="text-green-500 mr-1.5 font-bold">✓</span> {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mt-auto">
                        <div className="flex items-baseline justify-between mb-4">
                          <span className="text-2xl font-black text-primary-600">${pack.price}</span>
                          <span className="text-xs text-gray-400">Tax Included</span>
                        </div>
                        
                        <button 
                          onClick={() => handleAddToCart(pack)}
                          className="w-full bg-primary-600 text-white py-3 rounded-xl font-bold hover:bg-primary-700 transition-all shadow-md active:scale-95"
                        >
                          Book Pack
                        </button>
                      </div>
                    </div>
                </div>
              ))}
            </div>
        </div>
      </section>

      {/* Why Tutoring Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
              Real Humans, Real Results
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our native tutors are here to guide you through every step of your language journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-primary-50 to-secondary-50 border border-primary-100">
              <h3 className="text-xl font-display font-bold text-gray-900 mb-3">Native Experts</h3>
              <p className="text-gray-700">
                All our tutors are native speakers with certifications in teaching Spanish as a foreign language.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-secondary-50 to-primary-50 border border-secondary-100">
              <h3 className="text-xl font-display font-bold text-gray-900 mb-3">Flexible Schedules</h3>
              <p className="text-gray-700">
                Book sessions at your convenience. We offer 24/7 availability across different time zones.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-primary-50 to-secondary-50 border border-primary-100">
              <h3 className="text-xl font-display font-bold text-gray-900 mb-3">Custom Roadmap</h3>
              <p className="text-gray-700">
                Each session is tailored to your level, goals, and interests. Learn what you actually need.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: '#FF6B6B' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6">
            Ready to speak Spanish with confidence?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Choose your pack and start your first conversation within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-white text-primary-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-primary-50 transition shadow-lg"
            >
              Contact a Tutor
            </Link>
            <Link 
              to="/trial" 
              className="bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-600 transition"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Shopping Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowCart(false)}></div>
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-bold">Your Hub Cart</h2>
                <button
                  onClick={() => setShowCart(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {cart.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">Your cart is empty</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div key={item.cartItemId} className="flex items-center space-x-4 p-4 border rounded-lg">

                        <div className="flex-1">
                          <h3 className="font-semibold text-sm">{item.title || item.name}</h3>
                          <p className="text-gray-500 text-xs">{item.period}</p>
                          <p className="font-bold text-primary-600">${item.totalPrice.toFixed(2)}</p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.cartItemId)}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {cart.length > 0 && (
                <div className="border-t p-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-semibold">Total:</span>
                    <span className="text-2xl font-bold text-primary-600">
                      ${getTotalPrice().toFixed(2)}
                    </span>
                  </div>
                  <button 
                    onClick={handleCheckout}
                    className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition shadow-lg"
                  >
                    Schedule & Order
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Shared Footer Component */}
      <Footer />
    </div>
  );
}

export default SamplesPage;
